import { defaultPagination } from '@Core';
import { IGetCameraPhotoProps } from '@EcamModel/controllers/ICameraPhotoHttpController';
import { IUpdateEvidencePhoto } from '@EcamModel/controllers/IGroupPotentialContraventionHttpController';
import {
    CameraPhotoVehicles,
    CaptureType,
    GroupPotentialContraventions,
    PhotoType,
    PotentialContraventionWithInfo,
} from '@EcamModel/model';
import CloseIcon from '@assets/jsx-icon/CloseIcon';
import { BaseTextField } from '@components/BaseTextField';
import PopUpBase from '@components/PopUpBase';
import { pushError, pushSuccess } from '@components/toast';
import { appConfig } from '@configs/index';
import { cameraPhotoController, potentialGroupContraventionController } from '@controllers/index';
import { IPopUp } from '@hooks/usePopUp';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    Grow,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import { Filter } from '@pages/cameras/live-photos/components';
import BaseList from '@pages/cameras/live-photos/components/BaseList';
import { useVerificationQueueMode } from '@pages/verification/hooks/VerificationProvider';
import color from '@theme/Colors';
import _, { orderBy } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiInfo, FiSearch } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getSecond } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { usePotentialContravention } from '../hooks/PotentialContraventionProvider';
import { pcImage } from './EvidencePhotoGroup';

export type Props = Omit<IPopUp, 'onConfirm'> & {
    pc: PotentialContraventionWithInfo;
    onPreviewChange: (value: boolean) => void;
    onSetSelectedPhotos: (photo: CameraPhotoVehicles) => void;
};

type Column = {
    id: number;
    title: string;
    items: Partial<CameraPhotoVehicles[]>;
};

const photoType = {
    [PhotoType.Entry]: 'Entry photo',
    [PhotoType.Validation]: 'Contravention validation photo',
    [PhotoType.Exit]: 'Exit photo',
    [PhotoType.Overview]: 'Overview photo',
};

enum ModeButtonDisabled {
    initial,
    cleared,
}

export default function PopupChangeEvidence(props: Props) {
    const { pc, onSetSelectedPhotos, onPreviewChange } = props;
    const { verificationQueueMode } = useVerificationQueueMode();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const backdrop = useBackdrop();
    const potentialContraventionContext = usePotentialContravention();
    const { contraventionId, setGroupPc, selectedPc } = potentialContraventionContext;
    const { entryPhoto, validationPhoto, exitPhoto } = pcImage(selectedPc || pc);
    const searchPlateRef = useRef<HTMLInputElement | null>(null);

    const buildColumnItem = (photo?: CameraPhotoVehicles) => ({
        Id: photo?.Id,
        Plate: photo?.Plate!,
        CameraPhoto: photo?.CameraPhoto!,
        X1: photo?.X1,
        X2: photo?.X2,
        X3: photo?.X3,
        X4: photo?.X4,
        Y1: photo?.Y1,
        Y2: photo?.Y2,
        Y3: photo?.Y3,
        Y4: photo?.Y4,
    });

    const photoMap: { id: number; type: PhotoType; photo?: CameraPhotoVehicles }[] = [
        { id: 1, type: PhotoType.Entry, photo: entryPhoto },
        { id: 2, type: PhotoType.Validation, photo: validationPhoto },
        { id: 3, type: PhotoType.Exit, photo: exitPhoto },
    ];

    const initialColumnsEvidencePhoto: Column[] = photoMap
        .filter(({ photo }) => photo)
        .map(({ id, type, photo }) => ({
            id,
            title: photoType[type],
            items: [buildColumnItem(photo!)] as any,
        }));

    const [isCheckedDisableButtonClearOrResetItems, setIsCheckedDisableButtonClearOrResetItems] =
        useState<ModeButtonDisabled>(ModeButtonDisabled.initial);

    const [paging, setPaging] = useState(defaultPagination<CameraPhotoVehicles>());
    const initFilter: Filter = useMemo(() => {
        return {
            page: 1,
            pageSize: 20,
            timeFrom: moment()
                .tz(pc.Timezone || moment.tz.guess())
                .startOf('day')
                .toDate(),
            timeTo: moment()
                .tz(pc.Timezone || moment.tz.guess())
                .endOf('day')
                .toDate(),
            dayStartTime: null,
            dayEndTime: null,
            plate: pc.Plate ?? '',
            refetch: false,
        };
    }, [pc.Timezone]);

    const [filter, setFilter] = useState<Filter>(initFilter);
    const [livePhotosItems, setLivePhotosItems] = useState<CameraPhotoVehicles[]>([]);
    const [selectedColumnEvidencePhoto, setSelectedColumnEvidencePhoto] = useState<number | null>(null);
    const [selectedItemsLivePhotos, setSelectedItemsLivePhotos] = useState<CameraPhotoVehicles | null>(null);
    const [valueItemsLivePhotoSelected, setValueItemsLivePhotoSelected] =
        useState<Column[]>(initialColumnsEvidencePhoto);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [inputPlate, setInputPlate] = useState<string>(filter.plate ?? '');

    const debouncedUpdateFilter = useRef(
        _.debounce((val) => {
            setFilter((prev) => ({ ...prev, plate: val }));
        }, 500)
    ).current;

    const compareIds = (): boolean => {
        for (let i = 0; i < initialColumnsEvidencePhoto.length; i++) {
            if (initialColumnsEvidencePhoto[i].items[0]?.Id !== valueItemsLivePhotoSelected[i].items[0]?.Id) {
                return false;
            }
        }
        return true;
    };

    const isCheckDisabledButtonConfirm =
        valueItemsLivePhotoSelected.some((column) => column.items.length === 0) ||
        paging.rows.length === 0 ||
        compareIds();

    function updateFilter(params: Partial<Filter>) {
        setFilter((prev) => ({ ...prev, page: 1, ...params }));
    }

    function handleChangeTime(from: Moment | null, to: Moment | null) {
        let updatedFrom = from;
        let updatedTo = to;

        if (!updatedTo) {
            updatedTo = moment().endOf('day');
        }

        if (updatedFrom && updatedTo && updatedFrom.isAfter(updatedTo)) {
            [updatedFrom, updatedTo] = [updatedTo, updatedFrom];
        }

        updateFilter({ dayStartTime: updatedFrom, dayEndTime: updatedTo });
        setIsCheckedDisableButtonClearOrResetItems(ModeButtonDisabled.initial);
    }

    useEffect(() => {
        const existingIds = initialColumnsEvidencePhoto.flatMap((column) => column.items.map((item) => item?.Id));
        const filteredRows = paging.rows.filter((row) => !existingIds.includes(row.Id));
        setLivePhotosItems(filteredRows);
    }, [paging.rows]);

    const handleRadioChangeColumnEvidencePhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedColumnEvidencePhoto(Number(event.target.value));
    };
    const handleRadioChangeItemsLivePhotos = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedItem = livePhotosItems.find((item) => item.Id === Number(event.target.value));
        setSelectedItemsLivePhotos(selectedItem || null);
    };

    const handleChangeEvidence = () => {
        if (selectedColumnEvidencePhoto !== null && selectedItemsLivePhotos !== null) {
            const getCaptureAt = (columnId: number): Date | null => {
                const column = valueItemsLivePhotoSelected.find((col) => col.id === columnId);
                const captureAt = column?.items?.[0]?.CameraPhoto?.CaptureAt;
                return captureAt ? new Date(captureAt) : null;
            };

            const captureAt1 = getCaptureAt(1);
            const captureAt2 = getCaptureAt(2);
            const captureAt3 = getCaptureAt(3);
            const selectedCaptureAt = new Date(selectedItemsLivePhotos.CameraPhoto?.CaptureAt!);

            if (isNaN(selectedCaptureAt.getTime())) {
                pushError('Selected item CaptureAt is not a valid date');
                return;
            }

            if (selectedColumnEvidencePhoto === 2) {
                if (captureAt1 && selectedCaptureAt.getTime() <= captureAt1.getTime()) {
                    pushError('Contravention validation/Exit time must be after Entry time');
                    return;
                }
            } else if (selectedColumnEvidencePhoto === 3) {
                if (captureAt1 && selectedCaptureAt.getTime() <= captureAt1.getTime()) {
                    pushError('Contravention validation/Exit time must be after Entry time');
                    return;
                }
                if (captureAt2 && selectedCaptureAt.getTime() < captureAt2.getTime()) {
                    pushError('Exit time must be after Contravention validation time');
                    return;
                }
            } else if (selectedColumnEvidencePhoto === 1) {
                if (captureAt2 && captureAt2.getTime() <= selectedCaptureAt.getTime()) {
                    pushError('Entry time must be before Contravention validation time');
                    return;
                }
                if (captureAt3 && captureAt3.getTime() <= selectedCaptureAt.getTime()) {
                    pushError('Entry time must be before Exit time');
                    return;
                }
            }

            const updateColumn = (columnId: number) => {
                setValueItemsLivePhotoSelected((prevColumns) =>
                    prevColumns.map((column) => {
                        if (column.id === columnId) {
                            const oldItem = column.items?.[0];
                            column.items = [selectedItemsLivePhotos];
                            if (oldItem && oldItem.Id !== selectedItemsLivePhotos.Id) {
                                setLivePhotosItems((prevItems) => {
                                    const newItems = [...prevItems];
                                    const index = newItems.findIndex((item) => item.Id === oldItem.Id);
                                    if (index === -1) {
                                        newItems.push(oldItem);
                                    }
                                    return newItems;
                                });
                            }
                        }
                        return column;
                    })
                );
            };

            if (selectedColumnEvidencePhoto === 2) {
                updateColumn(2);
                updateColumn(3);
            } else if (selectedColumnEvidencePhoto === 3) {
                updateColumn(3);
            } else {
                updateColumn(selectedColumnEvidencePhoto);
            }

            setLivePhotosItems((prevItems) => {
                const newItems = prevItems.filter((item) => item.Id !== selectedItemsLivePhotos.Id);
                return newItems;
            });

            setSelectedItemsLivePhotos(null);
        }
    };

    const handleRemoveItem = (columnId: number, itemId: number) => {
        setValueItemsLivePhotoSelected((prevColumns) =>
            prevColumns.map((column) => {
                if (column.id === columnId) {
                    const updatedItems = column.items.filter((item) => item?.Id !== itemId);
                    const removedItem = column.items.find((item) => item?.Id === itemId);
                    if (removedItem) {
                        setLivePhotosItems((prevItems) => {
                            if (!prevItems.some((item) => item.Id === removedItem.Id)) {
                                return [...prevItems, removedItem];
                            }
                            return prevItems;
                        });
                    }
                    return {
                        ...column,
                        items: updatedItems,
                    };
                }
                return column;
            })
        );
    };


    const startOfHour: Moment = moment.tz(selectedPc?.EntryAt,selectedPc?.Timezone ?? moment.tz.guess()).startOf('day');
    const endOfHour: Moment = moment.tz(selectedPc?.EntryAt,selectedPc?.Timezone ?? moment.tz.guess()).endOf('day');

    const getPlateImageBundlePhotos = async () => {
        if (!selectedPc?.CameraId) return;
        setIsLoading(true);
        const _filter: IGetCameraPhotoProps = {
            page: filter.page,
            pageSize: filter.pageSize,
            sorts: ['-CaptureAt'],
            CameraId: selectedPc.CameraId ?? 0,
            FromDate: startOfHour.toDate(),
            ToDate: endOfHour.toDate(),
            FromTime: filter.dayStartTime ? getSecond(filter.dayStartTime) : undefined,
            ToTime: filter.dayEndTime ? getSecond(filter.dayEndTime) : undefined,
            Plate: filter.plate,
        };
        await cameraPhotoController
            .getCameraPhotoVehiclesByPlate(_filter)
            .then((res) => {
                setPaging(res);
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!props.open) return;
        getPlateImageBundlePhotos();
        setValueItemsLivePhotoSelected(initialColumnsEvidencePhoto);
    }, [
        filter.page,
        filter.timeFrom,
        filter.timeTo,
        filter.dayStartTime,
        filter.dayEndTime,
        filter.pageSize,
        filter.plate,
        props.open,
    ]);

    const handleChangeEvidencePhoto = async () => {
        backdrop.setTrue();
        const updateEvidencePhoto: IUpdateEvidencePhoto = {
            PotentialContraventionId: pc?.Id!,
            EntryPhotos: {
                CameraPhotoVehicleId: valueItemsLivePhotoSelected?.[0].items?.[0]?.Id!,
                PotentialContraventionId: pc?.Id!,
                PhotoType: PhotoType.Entry,
            },
            ValidationPhotos: {
                CameraPhotoVehicleId: valueItemsLivePhotoSelected?.[1].items?.[0]?.Id!,
                PotentialContraventionId: pc?.Id!,
                PhotoType: PhotoType.Validation,
            },
            ExitPhotos: {
                CameraPhotoVehicleId: valueItemsLivePhotoSelected?.[2].items?.[0]?.Id!,
                PotentialContraventionId: pc?.Id!,
                PhotoType: PhotoType.Exit,
            },
        };
        await potentialGroupContraventionController
            .doUpdateEvidencePhoto(updateEvidencePhoto)
            .then(async (res) => {
                queryClient.invalidateQueries(['trailAuditEventLogPCN']);
                await potentialGroupContraventionController
                    .getGroupPotentialContravention(pc?.GroupPotentialContraventionId!)
                    .then((res) => {
                        props.onClose?.();
                        pushSuccess('Change evidence successfully');
                        const _res: GroupPotentialContraventions = {
                            ...res,
                            PotentialContraventions: orderBy(res.PotentialContraventions, (pc) => -pc.Step),
                        };
                        setGroupPc(_res);
                    })
                    .catch((err) => navigate(`/${verificationQueueMode}`));
            })
            .catch((err) => {})
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const handleCancel = () => {
        props.onClose?.();
        setSelectedColumnEvidencePhoto(null);
        setSelectedItemsLivePhotos(null);
        setLivePhotosItems([]);
        setFilter(initFilter);
        setValueItemsLivePhotoSelected(initialColumnsEvidencePhoto);
        setIsCheckedDisableButtonClearOrResetItems(ModeButtonDisabled.initial);
        if (searchPlateRef.current) {
            searchPlateRef.current.value = '';
        }
    };

    const handleClearAllItems = () => {
        setIsCheckedDisableButtonClearOrResetItems(ModeButtonDisabled.cleared);
        setValueItemsLivePhotoSelected((prevColumns) => {
            const itemIds = new Set(livePhotosItems.map((item) => item.Id));

            prevColumns.forEach((column) => {
                column.items.forEach((item) => {
                    if (item?.Id && !itemIds.has(item.Id)) {
                        setLivePhotosItems((prevItems) => [...prevItems, item]);
                        itemIds.add(item.Id);
                    }
                });
            });
            return prevColumns.map((column) => ({ ...column, items: [] }));
        });
    };

    const handleResetItems = () => {
        setIsCheckedDisableButtonClearOrResetItems(ModeButtonDisabled.initial);
        const resetColumns = initialColumnsEvidencePhoto.map((column) => {
            const resetItems = column.items.map((item) => {
                const livePhotoItem = livePhotosItems.find((livePhoto) => livePhoto.Id === item?.Id);
                return livePhotoItem || item;
            });
            return { ...column, items: resetItems };
        });

        setValueItemsLivePhotoSelected(resetColumns);

        const newLivePhotosItems = livePhotosItems.filter(
            (livePhoto) =>
                !initialColumnsEvidencePhoto
                    .flatMap((column) => column.items.map((item) => item?.Id))
                    .includes(livePhoto.Id)
        );

        setLivePhotosItems(newLivePhotosItems);
    };

    useEffect(() => {
        if (!props.open) return;
        setInputPlate(filter.plate ?? '');
    }, [filter.plate, props.open]);

    useEffect(() => {
        setFilter((prev) => ({ ...prev, plate: selectedPc?.Plate ?? '' }));
    }, [selectedPc?.Plate]);

    return (
        <PopUpBase
            open={props.open}
            fixOverflow={true}
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                    },
                    '& .MuiPaper-root': {
                        overflowY: 'unset',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
                maxWidth: 'lg',
            }}
            onClose={props.onClose}
            onConfirm={() => {}}
            title={
                <Stack>
                    <Typography variant="h4">Change evidence photo</Typography>
                    <Typography variant="body1" textAlign={'center'} color={'#85858A'}>
                        {/* Drag and drop images from the live photo to replace the evidence photos */}
                    </Typography>
                </Stack>
            }
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            subTitleProps={{ sx: { color: 'gray' } }}
            minWidthButton={150}
            desc={
                <Stack direction={'column'} spacing={1} height={'100%'}>
                    <Stack spacing={1} bgcolor={color.grey100} padding={2} borderRadius={1}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant="h5">Evidence photo</Typography>
                            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleResetItems}
                                    disabled={
                                        isCheckedDisableButtonClearOrResetItems === ModeButtonDisabled.initial ||
                                        isLoading
                                    }
                                    sx={{ padding: '4px 8px', fontSize: 12 }}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleClearAllItems}
                                    disabled={
                                        isCheckedDisableButtonClearOrResetItems === ModeButtonDisabled.cleared ||
                                        isLoading
                                    }
                                    sx={{ padding: '4px 8px', fontSize: 12 }}
                                >
                                    Clear all
                                </Button>
                            </Stack>
                        </Stack>
                        <Stack
                            width={'100%'}
                            spacing={1}
                            direction={'row'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                        >
                            {valueItemsLivePhotoSelected.map((column) => (
                                <Stack
                                    key={column.id}
                                    width="100%"
                                    direction="column"
                                    spacing={1}
                                    padding="8px 16px"
                                    borderRadius={1}
                                    bgcolor="white"
                                    border={
                                        selectedColumnEvidencePhoto === column.id
                                            ? `1px solid ${color.success}`
                                            : 'none'
                                    }
                                >
                                    <FormControl>
                                        <RadioGroup
                                            row
                                            aria-labelledby="radio-buttons-group-label"
                                            name="radio-buttons-group"
                                            value={selectedColumnEvidencePhoto}
                                            onChange={handleRadioChangeColumnEvidencePhoto}
                                        >
                                            <Grid container xs={12}>
                                                <Grid>
                                                    <FormControlLabel
                                                        sx={{
                                                            '.MuiFormControlLabel-label': { mt: '3px !important' },
                                                        }}
                                                        control={<Radio value={column.id} />}
                                                        label={column.title}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </RadioGroup>
                                    </FormControl>
                                    <Stack spacing={1}>
                                        {column.items.length ? (
                                            <>
                                                {column.items.map((item, index) => {
                                                    return (
                                                        <PhotoDetails
                                                            isCheckIcon
                                                            key={index}
                                                            item={item}
                                                            isLoading={isLoading}
                                                            onSetSelectedItem={onSetSelectedPhotos}
                                                            onPreviewChange={onPreviewChange}
                                                            timezone={props.pc.Timezone}
                                                            handleRemoveItem={handleRemoveItem}
                                                            columnId={column.id}
                                                        />
                                                    );
                                                })}
                                            </>
                                        ) : (
                                            <ListNoData
                                                sx={{
                                                    minHeight: 207,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>

                    <Stack direction={'column'} bgcolor={color.grey100} padding={2} spacing={1} borderRadius={1}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <Typography variant="h5">Live photo</Typography>
                                <Button
                                    variant="outlinedGreen"
                                    sx={{ padding: '4px 8px', fontSize: 12 }}
                                    onClick={handleChangeEvidence}
                                    disabled={!selectedColumnEvidencePhoto || !selectedItemsLivePhotos?.Id}
                                >
                                    Change evidence
                                </Button>
                            </Stack>
                            <Stack direction={'row'} spacing={2} sx={{ width: { xs: '100%', md: '50%' } }}>
                                <BaseTextField
                                    placeholder={'E.g., ABC**, A**12'}
                                    inputRef={searchPlateRef}
                                    value={inputPlate}
                                    onChange={(e) => {
                                        setInputPlate(e.target.value);
                                        debouncedUpdateFilter(e.target.value);
                                    }}
                                    maxLength={10}
                                    InputProps={{
                                        endAdornment: (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <FiSearch
                                                    color="disabled"
                                                    size={16}
                                                    style={{ color: 'rgba(156, 154, 154, 0.38)', cursor: 'pointer' }}
                                                />
                                                <Tooltip
                                                    title="Use * as a wildcard. Example: 'ABC**' or 'A**12'"
                                                    placement="top"
                                                    arrow
                                                >
                                                    <span style={{ height: '14px' }}>
                                                        <FiInfo size={14} style={{ cursor: 'pointer' }} />
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        ),
                                    }}
                                />
                                <TimePicker
                                    label="From"
                                    value={filter.dayStartTime || null}
                                    ampm={false}
                                    onAccept={(newValue) => handleChangeTime(newValue, filter.dayEndTime)}
                                    sx={{ width: 260, '& .MuiSvgIcon-root': { width: 18, height: 18 } }}
                                />
                                <TimePicker
                                    label="To"
                                    ampm={false}
                                    disabled={!filter.dayStartTime}
                                    value={filter.dayEndTime || null}
                                    onAccept={(newValue) => handleChangeTime(filter.dayStartTime, newValue)}
                                    sx={{ width: 260, '& .MuiSvgIcon-root': { width: 18, height: 18 } }}
                                />
                            </Stack>
                        </Stack>
                        <Stack
                            sx={{
                                maxHeight: 220,
                                overflowY: 'auto',
                                '::-webkit-scrollbar': { width: '2px' },
                            }}
                            pb={2}
                        >
                            {isLoading ? (
                                <Stack
                                    width={'100%'}
                                    direction="row"
                                    justifyContent="center"
                                    alignItems="center"
                                    my={10}
                                >
                                    <Grow in={true} timeout={Math.min(500, 1000)}>
                                        <CircularProgress />
                                    </Grow>
                                </Stack>
                            ) : (
                                <>
                                    <BaseList
                                        list={livePhotosItems}
                                        renderItem={(photo) => (
                                            <Box
                                                key={photo.Id}
                                                bgcolor={color.white}
                                                p={1}
                                                border={1}
                                                borderColor={color.grey200}
                                                borderRadius={1}
                                                position={'relative'}
                                            >
                                                <FormControl>
                                                    <RadioGroup
                                                        row
                                                        aria-labelledby="radio-buttons-group-label"
                                                        name="radio-buttons-group"
                                                        value={selectedItemsLivePhotos?.Id || ''}
                                                        onChange={handleRadioChangeItemsLivePhotos}
                                                    >
                                                        <Grid container>
                                                            <Grid item>
                                                                <FormControlLabel
                                                                    sx={{ ml: '-6px !important' }}
                                                                    control={<Radio value={photo.Id ?? 0} />}
                                                                    label={photo.Id ?? 0}
                                                                    disabled={!selectedColumnEvidencePhoto}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </RadioGroup>
                                                </FormControl>
                                                <PhotoDetails
                                                    key={photo.Id}
                                                    item={photo}
                                                    timezone={props.pc.Timezone}
                                                    onSetSelectedItem={onSetSelectedPhotos}
                                                    onPreviewChange={onPreviewChange}
                                                />
                                            </Box>
                                        )}
                                        count={paging.total}
                                        page={filter.page}
                                        pageSize={filter.pageSize}
                                        onChangePage={(page, pageSize) => {
                                            setFilter((prev) => ({ ...prev, page, pageSize }));
                                            setIsCheckedDisableButtonClearOrResetItems(ModeButtonDisabled.initial);
                                        }}
                                        renderEmpty={() => (
                                            <Stack
                                                width="100%"
                                                minHeight={200}
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Typography variant="h2" fontWeight={400} color="primary.dark">
                                                    {'No results found'}
                                                </Typography>
                                                <Typography variant="h6" fontWeight={400} align="center">
                                                    <>
                                                        We couldn't find what searched for. <br />
                                                        Try searching again.
                                                    </>
                                                </Typography>
                                            </Stack>
                                        )}
                                    />
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            }
            customActions={
                <Stack mt={3} direction={'row'} width="100%" justifyContent="space-between">
                    <Button sx={{ minWidth: 130 }} variant="cancel" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={isCheckDisabledButtonConfirm}
                        onClick={() => {
                            handleChangeEvidencePhoto();
                            handleCancel();
                        }}
                    >
                        Confirm
                    </Button>
                </Stack>
            }
        />
    );
}

export const PhotoDetails = ({
    handleRemoveItem,
    onPreviewChange,
    onSetSelectedItem,
    item,
    columnId,
    isCheckIcon,
    isLoading,
    timezone,
}: {
    item: CameraPhotoVehicles | undefined;
    onSetSelectedItem: (item: CameraPhotoVehicles) => void;
    onPreviewChange: (open: boolean) => void;
    handleRemoveItem?: (columnId: number, itemId: number) => void;
    columnId?: number;
    isCheckIcon?: boolean;
    isLoading?: boolean;
    timezone?: string;
}) => {
    console.log('🚀 ~ item:', item);
    return (
        <Box position={'relative'}>
            <Box
                sx={{
                    width: '100%',
                    height: '120px',
                    background: `url(${appConfig.gateway.blobURL}${item?.CameraPhoto?.BlobName}) no-repeat center center`,
                    backgroundSize: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                }}
                onClick={() => {
                    if (!item) return;
                    onSetSelectedItem(item);
                    onPreviewChange(true);
                }}
            />
            <Stack spacing={0.5} mt={1}>
                <Typography variant="body2">
                    Plate:
                    <Box component={item?.Plate.length === 7 ? 'mark' : 'span'}>
                        {item?.Plate || 'Unable to recognize license plate'}
                    </Box>
                </Typography>

                <Typography variant="body2">
                    On date:{' '}
                    {moment(item?.CameraPhoto?.CaptureAt || 0)
                        .tz(timezone || moment.tz.guess())
                        .format('DD/MM/YYYY HH:mm:ss')}
                </Typography>
                <Typography variant="body2">
                    Capture type: {item?.CameraPhoto?.CaptureType === CaptureType.trigged ? 'Triggered' : 'Command'}
                </Typography>
                <Typography variant="body2">
                    Within operational hours: {item?.CameraPhoto?.IsWithinOperationalHours}
                </Typography>
            </Stack>
            {isCheckIcon && (
                <IconButton
                    sx={{
                        position: 'absolute',
                        top: -3,
                        right: -3,
                    }}
                    disabled={isLoading}
                    onClick={() => handleRemoveItem?.(columnId ?? 0, item?.Id ?? 0)}
                >
                    <CloseIcon stroke={color.white} cursor={isLoading ? 'default' : 'pointer'} />
                </IconButton>
            )}
        </Box>
    );
};
